import { convertMdiBracketToIcon } from '@/mdi';
import * as turf from "@turf/turf";
import { FeatureCollection, MultiPolygon, Polygon } from 'geojson';
import Mapbox from 'index.vue';
import { BaseFeatureLayer } from './baseFeatureLayer';
import i18n from '../../../main';
export class CatchmentAreaFeatureLayerHelper {
    
    protected mapboxInstance: InstanceType<typeof Mapbox>;
    protected zonesLayer: BaseFeatureLayer;
    protected impactLayer: BaseFeatureLayer;
    //protected zoneOutlinesLayer: BaseFeatureLayer;
    protected areasOutlinesLayer: BaseFeatureLayer;
    constructor(mapboxInstance: InstanceType<typeof Mapbox>) {
        this.mapboxInstance = mapboxInstance;
        this.areasOutlinesLayer = new BaseFeatureLayer(mapboxInstance, 'top', 'ca-areas-layer', 'ca-areas-source', 'line', {}, {
            'line-color': '#333333', // Border color
            'line-opacity': 0.3,
            'line-width': 1
        });

        this.zonesLayer = new BaseFeatureLayer(mapboxInstance, 'top','ca-zones-layer', 'ca-zones-source', 'fill', {}, {
            'fill-color': ['get', 'color'],
            'fill-opacity': ['get', 'opacity'],
        },true,true);
        this.impactLayer = new BaseFeatureLayer(mapboxInstance, 'top', 'ca-impact-layer', 'ca-impact-source', 'fill', {}, {
            'fill-color': [
                'case',
                ['>', ['get', 'impact'], 0], '#55ff55', // Green if impact < 0
                ['<', ['get', 'impact'], 0], '#ff5555', // Red if impact > 0
                '#CCCCCC' // Default color (gray) if impact == 0 or missing
            ],
            'fill-opacity': 0.6,
        },true);
    }
    
    private darkenHexColor(hex: string, percent: number): string {
        // Ensure it starts with #
        if (!hex.startsWith('#')) {
            hex = '#' + hex;
        }
        // Convert hex to RGB
        const num = parseInt(hex.slice(1), 16);
        let r = (num >> 16) & 0xff;
        let g = (num >> 8) & 0xff;
        let b = num & 0xff;
        // Apply darkening
        r = Math.max(0, Math.min(255, Math.floor(r * (1 - percent))));
        g = Math.max(0, Math.min(255, Math.floor(g * (1 - percent))));
        b = Math.max(0, Math.min(255, Math.floor(b * (1 - percent))));
        // Convert back to hex
        const darkHex = '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase();
        return darkHex;
    }

    private mergeSelectedFeatures(features: any) {
        if (features.length === 0) return null;
        // Ensure all features are valid Polygon or MultiPolygon
        const validFeatures = features.filter((f : any) =>
            f.geometry.type === "Polygon" || f.geometry.type === "MultiPolygon"
        );
        // Ensure features are valid GeoJSON features
        const featureCollection: FeatureCollection<Polygon | MultiPolygon> = turf.featureCollection(validFeatures);
        // Merge polygons
        let mergedFeature: any = featureCollection.features[0]
        if (featureCollection.features.length > 1) {
            const feature = featureCollection?.features[0];
            mergedFeature = turf.union(featureCollection);
            mergedFeature.properties.id = feature.properties?.id;
        }
        return mergedFeature;
    }

    // Modified groupBy function
    private groupBy = <T, K extends keyof any>(
        arr: T[],
        key: (item: T) => K
    ): Array<{ key: K; items: T[] }> => {
        // Use a Map to group items by the key
        const map = new Map<K, T[]>();

        arr.forEach((item) => {
            const groupKey = key(item);
            const group = map.get(groupKey);
            if (group) {
                group.push(item);
            } else {
                map.set(groupKey, [item]);
            }
        });
        // Convert the Map into an array of objects
        return Array.from(map, ([key, items]) => ({ key, items }));
    };
    
    private getRow(fieldName: any, value: any, colorResult: boolean = false, postFix: string = '') {
        let value2 = value;
        if (colorResult && value > 0) value2 = '<span class="positiveResult">+' +  value + postFix+ '</span>';
        if (colorResult && value < 0) value2 = '<span class="negativeResult">' + value + postFix +'</span>';
        return '<div class="tbdc-row"><div>' + fieldName + '</div><div style="text-align: right; margin-left: 1em;">' + value2 + '</div><div></div></div>';
    }

    public DataChanged = async () => {
        const _self = this;
        let maximumFractionDiff = 0;
        
        if (this.mapboxInstance.catchmentArea == null || this.mapboxInstance.locationCatchmentArea == null || _self.mapboxInstance.locationCatchmentArea.featureCollection == null) {
            this.mapboxInstance.locationCatchmentAreaFeatures = null;
            this.zonesLayer.SetFeatures([]);
            this.impactLayer.SetFeatures([]);
            this.areasOutlinesLayer.SetFeatures([]);
        } else {
            const maximumDelta = this.mapboxInstance.locationCatchmentArea.featureCollection.features.reduce((maxDelta: number, feature: any) => { return Math.max(maxDelta, Math.abs(feature.properties.fraction)); }, 0);
            const zonesFeatures : any[] = [];
            const impactFeatures: any[] = [];
            const zones = this.groupBy(this.mapboxInstance.locationCatchmentArea.featureCollection.features, (i: any) => i.properties.locationPropertyId);
            const outlineColor = this.firstLegendColor();
            zones.forEach((group: any) => {
                if (group.key == null) {
                    //Score model
                    group.items.forEach((feature: any) => {
                        if (feature.properties.valueSharePrevious <= _self.mapboxInstance.catchmentArea.coverage) {
                            const fractionDiff = Math.min(1.0, 3 / (maximumDelta / Math.abs(feature.properties.fraction)));
                            if (fractionDiff > maximumFractionDiff) maximumFractionDiff = fractionDiff;
                            feature.properties.color = _self.pickHex([0, 96, 127], [75, 225, 255], fractionDiff);
                            feature.properties['opacity'] = 0.7;
                            feature.properties['titleHtml'] = '<h3>' + feature.properties.areaCode + ' (' + Math.round(feature.properties.fraction * 100) + '%)</h3>';
                            zonesFeatures.push(feature);
                        }
                        if (_self.mapboxInstance.location.changeType == 0 && _self.mapboxInstance.scenario.parentScenarioId != null && !_self.mapboxInstance.locationCatchmentArea.sameAsParent && feature.properties.value != feature.properties.valueDefault && feature.properties.value - feature.properties.valueDefault != 0) {
                            const impact = feature.properties.value - feature.properties.valueDefault;
                            feature.properties['impact'] = impact;
                            feature.properties['titleHtml'] += '<div class="tbdc-grid">' + _self.getRow('Impact', Math.round((feature.properties.fraction - feature.properties.fractionDefault) * 100),true,'%') + '</div>';
                            impactFeatures.push(feature);
                        }
                        feature.properties['outline-color'] = '#00607F';
                    });

                    
                }
                else
                {
                    //NOI model
                    const locationProperty = _self.mapboxInstance.locationCatchmentArea.locationProperties.find((property: any) => property.id == group.key);
                    const zoneFeature = _self.mergeSelectedFeatures(group.items.filter((feature: any) => {
                        return feature.properties.fraction >= 0.01 || feature.properties.fractionDefault >= 0.01;
                    }));
                    if (zoneFeature && zoneFeature.properties) {
                        zoneFeature.properties['color'] = _self.removeOpacityFromColor(locationProperty.catchmentAreaZoneColor);
                        zoneFeature.properties['outline-color'] = this.darkenHexColor(_self.removeOpacityFromColor(outlineColor), 0.2);
                        

                        let nonShared = 0;
                        let total = 0;
                        let impact = 0;
                        group.items.forEach(function (feature: any) {
                            total += feature.properties.value;
                            impact += feature.properties.value - feature.properties.valueDefault;
                            if (Math.round(feature.properties.fraction * 100) / 100 === 1) nonShared += feature.properties.value;
                        });
                        total = Math.round(total);
                        nonShared = Math.round(nonShared);
                        impact = Math.round(impact);
                        zoneFeature.properties['titleHtml'] = '<h3>' + convertMdiBracketToIcon(locationProperty.title) + '</h3><div class="tbdc-grid">' + _self.getRow('Total',total,false) + _self.getRow(i18n.t('message.nonShared'), nonShared) + _self.getRow(i18n.t('message.shared'),(total - nonShared)) + '</div>';
                        zoneFeature.properties['opacity'] = 0.8;
                        zonesFeatures.push(zoneFeature);
                        //Get the features with impact
                        group.items.forEach(function (feature: any) {
                            if (_self.mapboxInstance.location.changeType == 0 && _self.mapboxInstance.scenario.parentScenarioId != null && !_self.mapboxInstance.locationCatchmentArea.sameAsParent && feature.properties.fraction != feature.properties.fractionDefault) {
                                const areaImpact = Math.round(feature.properties.value - feature.properties.valueDefault);
                                feature.properties['impact'] = impact;
                                feature.properties['titleHtml'] = zoneFeature.properties['titleHtml'] + '<h3>' + i18n.t('message.impact') + '</h3><div class="tbdc-grid">' + _self.getRow('Total', impact, true) + _self.getRow('On area ' + feature.properties.areaCode, areaImpact, true) + '</div>';
                                impactFeatures.push(feature);
                            }
                        });
                    }
                }
                
            });

            const excludedFeatures = this.mapboxInstance.locationCatchmentArea.featureCollection.features.filter((feature: any) => {
                return feature.properties &&feature.properties.fraction < 0.01;
            });
            const excludedFeature = _self.mergeSelectedFeatures(excludedFeatures);
            if (excludedFeature && excludedFeature.properties) {
                excludedFeature.properties['color'] = '#999999';
                excludedFeature.properties['excluded'] = true;
                excludedFeature.properties['titleHtml'] = '<h3>Excluded</h3>';
                excludedFeature.properties['opacity'] = 0.25; 
                zonesFeatures.push(excludedFeature);
            }

            const areas = this.mapboxInstance.locationCatchmentArea.featureCollection.features.filter((feature: any) => {
                //select all area with a fraction > 0 and where it is included in the catchment area (score modl)
                return feature.properties && feature.properties.fraction >= 0.01 && feature.properties.valueSharePrevious <= _self.mapboxInstance.catchmentArea.coverage;
            });
            this.areasOutlinesLayer.SetFeatures(areas);
            this.zonesLayer.SetFeatures(zonesFeatures);
            this.impactLayer.SetFeatures(impactFeatures);
            this.createLegend(maximumFractionDiff);
        }
    };

    

    private createLegend(maximumFractionDiff: number) {
        const legend = [];
        const _self = this;
        if (this.mapboxInstance.catchmentArea != null) {

            if (this.mapboxInstance.model.catchmentAreaZones.length > 0) {
                this.mapboxInstance.model.catchmentAreaZones.forEach(function (catchmentAreaZone: any) {
                    _self.mapboxInstance.model.catchmentAreas.forEach(function (catchmentArea: any) {
                        if (catchmentArea.name == catchmentAreaZone.catchmentAreaName && catchmentArea.id == _self.mapboxInstance.catchmentArea.catchmentAreaId) {
                            legend.push(catchmentAreaZone);
                        }
                    });
                })
            } else {
                legend.push({ title: '100%', color: _self.pickHex([0, 96, 127], [75, 225, 255], maximumFractionDiff) });
                legend.push({ title: '80%', color: _self.pickHex([0, 96, 127], [75, 225, 255], maximumFractionDiff * 0.8) });
                legend.push({ title: '60%', color: _self.pickHex([0, 96, 127], [75, 225, 255], maximumFractionDiff * 0.6) });
                legend.push({ title: '40%', color: _self.pickHex([0, 96, 127], [75, 225, 255], maximumFractionDiff * 0.4) });
                legend.push({ title: '20%', color: _self.pickHex([0, 96, 127], [75, 225, 255], maximumFractionDiff * 0.2) });
            }

        }
        this.mapboxInstance.$emit('legendChanged', legend);
    }


    private firstLegendColor() {
        if (this.mapboxInstance.catchmentArea != null) {
            if (this.mapboxInstance.model.catchmentAreaZones.length > 0) {
                for (const catchmentAreaZone of this.mapboxInstance.model.catchmentAreaZones) {
                    for (const catchmentArea of this.mapboxInstance.model.catchmentAreas) {
                        if (
                            catchmentArea.name === catchmentAreaZone.catchmentAreaName &&
                            catchmentArea.id === this.mapboxInstance.catchmentArea.catchmentAreaId
                        ) {
                            return catchmentAreaZone.color;
                        }
                    }
                }
            } else {
                return this.pickHex([0, 96, 127], [75, 225, 255], 0);
            }
        }
        return '#000000';
    }


    private pickHex(color1: any, color2: any, weight: any) {
        const w1 = weight;
        const w2 = 1 - w1;
        const rgb = [Math.round(color1[0] * w1 + color2[0] * w2),
        Math.round(color1[1] * w1 + color2[1] * w2),
        Math.round(color1[2] * w1 + color2[2] * w2)];
        const h = rgb[0] * 0x10000 + rgb[1] * 0x100 + rgb[2] * 0x1;
        return '#' + ('000000' + h.toString(16)).slice(-6);
    }

    private removeOpacityFromColor(color: any) {
        // Check if the color is an 8-digit hex (with opacity)
        if (/^#([0-9a-fA-F]{8})$/.test(color)) {
            return color.slice(0, 7); // Remove the last two characters (opacity)
        }

        // If it's already a 6-digit hex or invalid, return as-is
        return color;
    }

    
}   
